import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BedDouble, Search } from "lucide-react";
import { BasesTabla } from "@/components/bases/BasesTabla";
import { BasesInventario } from "@/components/bases/BasesInventario";
import { ExportBar, TAM_LBL, TAMANOS } from "@/components/bases/BasesComun";

const hoyGT = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Guatemala" });
const TABS = [["activos", "Activos del día"], ["fabricadas", "Fabricadas del día"], ["historial", "Historial"], ["nueva", "Nueva solicitud"], ["solicitadas", "Bases solicitadas"], ["inventario", "Inventario"]];
const FAB_COLS = [{ k: "cantidad_hecha", l: "Hechas ahora" }, { k: "madera", l: "Madera" }, { k: "hora", l: "Fecha y hora" }, { k: "por", l: "Por" }];

const useRows = (url, params, deps) => {
  const [rows, setRows] = useState(null);
  const load = () => api.get(url, { params }).then(({ data }) => setRows(data || [])).catch((e) => toast.error(formatApiError(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, 30000); return () => clearInterval(t); }, deps);
  return [rows, load];
};

const Buscador = ({ rows, children }) => {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const f = (rows || []).filter((r) => !s || [r.numero, r.pedido, r.cliente, r.producto, r.tamano, r.medidas].some((v) => (v || "").toLowerCase().includes(s)));
  return (
    <>
      <div className="relative max-w-md"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar solicitud, pedido, cliente, tamaño…" className="pl-9" data-testid="bases-search" /></div>
      {rows === null ? <div className="text-sm text-zinc-500">Cargando…</div> : children(f)}
    </>
  );
};

const Rango = ({ desde, hasta, setDesde, setHasta }) => (
  <div className="flex items-end gap-2">
    <label className="text-xs text-zinc-500">Desde<Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9" data-testid="bases-desde" /></label>
    <label className="text-xs text-zinc-500">Hasta<Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9" data-testid="bases-hasta" /></label>
  </div>
);

const Activos = () => {
  const [rows, load] = useRows("/bases/solicitudes", { vista: "activos" }, []);
  return <div className="space-y-3"><div className="flex justify-end"><ExportBar seccion="activos" /></div><Buscador rows={rows}>{(f) => <BasesTabla rows={f} onChanged={load} />}</Buscador></div>;
};

const Fabricadas = ({ historial }) => {
  const ayer = new Date(Date.now() - 86400000).toLocaleDateString("en-CA", { timeZone: "America/Guatemala" });
  const [desde, setDesde] = useState(historial ? ayer.slice(0, 8) + "01" : hoyGT());
  const [hasta, setHasta] = useState(historial ? ayer : hoyGT());
  const [rows, load] = useRows("/bases/fabricadas", { desde, hasta }, [desde, hasta]);
  const total = (rows || []).reduce((s, r) => s + r.cantidad_hecha, 0);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        {historial ? <Rango desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} /> : <div className="text-sm">Hoy {hoyGT()} · <b data-testid="bases-fab-total">{total}</b> base(s) terminadas</div>}
        <ExportBar seccion={historial ? "historial" : "fabricadas"} desde={desde} hasta={hasta} />
      </div>
      {historial && <div className="text-sm">Total del período: <b data-testid="bases-hist-total">{total}</b></div>}
      <Buscador rows={rows}>{(f) => <BasesTabla rows={f} onChanged={load} editable={false} extraCols={FAB_COLS} />}</Buscador>
    </div>
  );
};

const Nueva = ({ onCreated }) => {
  const [prods, setProds] = useState([]);
  const vacio = { tamano: "IMPERIAL", cantidad: 1, medidas: "", product_id: "", observaciones: "", prioridad: false };
  const [f, setF] = useState(vacio);
  useEffect(() => { api.get("/bases/config").then(({ data }) => setProds(data.productos || [])).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await api.post("/bases/solicitudes", { ...f, cantidad: parseInt(f.cantidad, 10) }); toast.success(`Solicitud ${data.numero} creada`); setF(vacio); onCreated(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const sel = "h-9 w-full rounded border border-zinc-300 px-2 text-sm bg-white";
  return (
    <div className="max-w-xl space-y-3 border border-zinc-200 rounded-md bg-white p-5" data-testid="bases-nueva">
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-zinc-500">Tamaño<select className={sel} value={f.tamano} onChange={(e) => setF({ ...f, tamano: e.target.value })} data-testid="bases-nueva-tamano">{TAMANOS.map((t) => <option key={t} value={t}>{TAM_LBL[t]}</option>)}</select></label>
        <label className="text-xs text-zinc-500">Cantidad<Input type="number" min="1" value={f.cantidad} onChange={(e) => setF({ ...f, cantidad: e.target.value })} data-testid="bases-nueva-cantidad" /></label>
      </div>
      <label className="text-xs text-zinc-500 block">Modelo (define el consumo de madera)<select className={sel} value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} data-testid="bases-nueva-modelo"><option value="">Base estándar (1 juego)</option>{prods.map((p) => <option key={p.id} value={p.id}>{p.nombre} · {p.juegos} juego(s)</option>)}</select></label>
      <label className="text-xs text-zinc-500 block">Medidas<Input value={f.medidas} onChange={(e) => setF({ ...f, medidas: e.target.value })} placeholder="Ej. 1.40 × 1.90" data-testid="bases-nueva-medidas" /></label>
      <label className="text-xs text-zinc-500 block">Observaciones<textarea rows={3} className="w-full rounded border border-zinc-300 p-2 text-sm" value={f.observaciones} onChange={(e) => setF({ ...f, observaciones: e.target.value })} data-testid="bases-nueva-obs" /></label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.prioridad} onChange={(e) => setF({ ...f, prioridad: e.target.checked })} data-testid="bases-nueva-prioridad" /> Prioridad</label>
      <Button onClick={save} className="bg-[#002FA7]" disabled={!f.cantidad} data-testid="bases-nueva-guardar">Crear solicitud</Button>
    </div>
  );
};

const Solicitadas = () => {
  const [rows, load] = useRows("/bases/solicitudes", { vista: "todas" }, []);
  const [desde, setDesde] = useState(hoyGT().slice(0, 8) + "01");
  const [hasta, setHasta] = useState(hoyGT());
  const enRango = (rows || []).filter((r) => (r.creada || "").slice(0, 10) >= desde && (r.creada || "").slice(0, 10) <= hasta);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2"><Rango desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} /><ExportBar seccion="solicitudes" desde={desde} hasta={hasta} /></div>
      <Buscador rows={rows === null ? null : enRango}>{(f) => <BasesTabla rows={f} onChanged={load} extraCols={[{ k: "creada", l: "Creada" }]} />}</Buscador>
    </div>
  );
};

export default function Bases() {
  const [tab, setTab] = useState("activos");
  return (
    <div className="space-y-6" data-testid="bases-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Producción</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3"><BedDouble className="w-8 h-8 text-[#B45309]" /> Bases</h1>
        <p className="text-zinc-500 mt-2 text-sm">Fabricación de bases, solicitudes automáticas de tamaño o producto especial e inventario de madera y bases.</p>
      </header>
      <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`bases-tab-${v}`}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === v ? "border-[#B45309] text-[#B45309] font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>{l}</button>
        ))}
      </div>
      {tab === "activos" && <Activos />}
      {tab === "fabricadas" && <Fabricadas />}
      {tab === "historial" && <Fabricadas historial key="h" />}
      {tab === "nueva" && <Nueva onCreated={() => setTab("activos")} />}
      {tab === "solicitadas" && <Solicitadas />}
      {tab === "inventario" && <BasesInventario />}
    </div>
  );
}
